import { type FC, Suspense } from "react";
import { Await } from "react-router";

import { PlatformIcons } from "~/components/UI/PlatformIcons";
import type { IMarketGroupChanceHistory } from "~/feature/chart/types";
import {
  dropUnavailableMarkets,
  hasAvailableChance,
} from "~/feature/chart/utils";
import {
  EmbedSparkline,
  EmbedSparklineSkeleton,
} from "~/feature/embed/components/EmbedSparkline";
import {
  EMBED_CONSENSUS_LABEL,
  EMBED_POWERED_BY,
  EMBED_VIEW_EVENT,
  EMBED_VOLUME_SUFFIX,
} from "~/feature/embed/constants";
import type { EmbedChanceHistory } from "~/feature/embed/types";
import { getEmbedOddsColumns } from "~/feature/embed/utils/getEmbedMinSize";
import { getEmbedRowLabels } from "~/feature/embed/utils/getEmbedRowLabels";
import { getEmbedTitle } from "~/feature/embed/utils/getEmbedTitle";
import type {
  IAggregatedEvent,
  IMarket,
  IOutcomeConsensus,
} from "~/types/event";
import { formatPercentageValue, formatValueFloor } from "~/utils/formatValue";

import * as Styled from "./EmbedCard.styles";

interface IEmbedCardProps {
  event: IAggregatedEvent;
  markets: IMarket[];
  outcome: IOutcomeConsensus | null;
  chanceHistory: EmbedChanceHistory;
  eventUrl: string;
  chart: boolean;
  odds: boolean;
  border: boolean;
  isConsensusMode: boolean;
  consensusOnly: boolean;
}

const toPercentage = (chance: number | null | undefined): string =>
  chance === null || chance === undefined
    ? "N/A"
    : formatPercentageValue(chance * 100, { maximumFractionDigits: 1 });

export const EmbedCard: FC<IEmbedCardProps> = ({
  event,
  markets,
  outcome,
  chanceHistory,
  eventUrl,
  chart,
  odds,
  border,
  isConsensusMode,
  consensusOnly,
}) => {
  const [firstMarket] = markets;
  const consensusChance = outcome?.chance ?? null;
  const drawConsensus = isConsensusMode && consensusChance !== null;
  // The event page's rule (PH-1116): a market nobody has traded has no line,
  // just as its row has no chance.
  const renderSparkline = (history: IMarketGroupChanceHistory[]) => (
    <EmbedSparkline
      history={dropUnavailableMarkets(history, markets, {
        keepConsensus: drawConsensus,
      })}
      markets={markets.filter(hasAvailableChance)}
      isConsensusMode={drawConsensus}
      consensusOnly={consensusOnly}
    />
  );
  const showConsensusRow = isConsensusMode;
  const showPlatformRows = !(isConsensusMode && consensusOnly);
  const columns = getEmbedOddsColumns(showPlatformRows ? markets.length : 0);
  const rowLabels = getEmbedRowLabels(markets);

  return (
    <Styled.Root $border={border}>
      <Styled.Header>
        {(firstMarket?.image || event.imageUrl) && (
          <Styled.Icon src={firstMarket?.image || event.imageUrl} alt="" />
        )}
        <Styled.Title>{getEmbedTitle(markets, event.title)}</Styled.Title>
      </Styled.Header>

      {chart &&
        (Array.isArray(chanceHistory) ? (
          renderSparkline(chanceHistory)
        ) : (
          <Suspense fallback={<EmbedSparklineSkeleton />}>
            <Await resolve={chanceHistory} errorElement={null}>
              {renderSparkline}
            </Await>
          </Suspense>
        ))}

      {odds && (
        <Styled.Odds $columns={columns}>
          {showPlatformRows &&
            markets.map((market, index) => (
              <Styled.Row key={market.id} $consensus={false}>
                <PlatformIcons platforms={market.platform} size={18} />
                <Styled.RowLabel $consensus={false} title={rowLabels[index]}>
                  {rowLabels[index]}
                </Styled.RowLabel>
                <Styled.RowValue>
                  {toPercentage(
                    hasAvailableChance(market) ? market.chance : null
                  )}
                </Styled.RowValue>
              </Styled.Row>
            ))}

          {showConsensusRow && (
            <Styled.Row $consensus $fullWidth>
              <Styled.BrandLogo sx={{ fontSize: 18 }} />
              <Styled.RowLabel $consensus>
                {EMBED_CONSENSUS_LABEL}
              </Styled.RowLabel>
              <Styled.RowValue>{toPercentage(consensusChance)}</Styled.RowValue>
            </Styled.Row>
          )}
        </Styled.Odds>
      )}

      <Styled.Footer>
        <Styled.Volume>
          {formatValueFloor(event.volume ?? 0)} {EMBED_VOLUME_SUFFIX}
        </Styled.Volume>
        <Styled.Branding>
          <Styled.BrandingLeft>
            <Styled.BrandLogo sx={{ fontSize: 14 }} />
            <Styled.BrandingText>{EMBED_POWERED_BY}</Styled.BrandingText>
          </Styled.BrandingLeft>
          <Styled.ViewEvent
            href={eventUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {EMBED_VIEW_EVENT}
          </Styled.ViewEvent>
        </Styled.Branding>
      </Styled.Footer>
    </Styled.Root>
  );
};
